import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Calendar, IndianRupee, TrendingUp, Download, Users, FileBarChart, CreditCard, Banknote, Smartphone, CheckSquare, Upload, Image, Eye, X } from 'lucide-react';
import { api } from '../lib/api';
import { authService } from '../lib/auth';

const MODE_LABELS = {
  cash: 'Cash',
  upi: 'UPI',
  credit_card: 'Credit Card',
  debit_card: 'Debit Card',
  bank_transfer: 'Bank Transfer',
  cheque: 'Cheque'
};

const MODE_COLORS = {
  cash: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
  upi: { bg: 'bg-violet-100', text: 'text-violet-600' },
  credit_card: { bg: 'bg-blue-100', text: 'text-blue-600' },
  debit_card: { bg: 'bg-orange-100', text: 'text-orange-600' },
  bank_transfer: { bg: 'bg-cyan-100', text: 'text-cyan-600' },
  cheque: { bg: 'bg-slate-100', text: 'text-slate-600' }
};

const OfflineReports = () => {
  const [activeTab, setActiveTab] = useState('admissions');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [batches, setBatches] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [depositedDates, setDepositedDates] = useState(new Set());
  const [depositSlips, setDepositSlips] = useState({});
  const [uploadingDate, setUploadingDate] = useState(null);
  const [previewSlip, setPreviewSlip] = useState(null);
  const fileInputRefs = useRef({});
  const user = authService.getCurrentUser();
  
  // Filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [counsellorFilter, setCounsellorFilter] = useState('');
  const [groupBy, setGroupBy] = useState('daily');

  useEffect(() => {
    fetchBatches();
    fetchCounsellors();
  }, []);

  useEffect(() => {
    fetchReport();
  }, [activeTab, startDate, endDate, batchFilter, counsellorFilter, groupBy]);

  const fetchBatches = async () => {
    try {
      const response = await api.getOfflineBatches({});
      setBatches(response.data || []);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const fetchCounsellors = async () => {
    try {
      const response = await api.getUsers('counsellor');
      setCounsellors(response.data || []);
    } catch (error) {
      console.error('Failed to fetch counsellors:', error);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (batchFilter) params.batch_id = batchFilter;
      if (counsellorFilter) params.counsellor_id = counsellorFilter;
      params.group_by = groupBy;
      
      let response;
      if (activeTab === 'admissions') {
        response = await api.getOfflineAdmissionReport(params);
      } else {
        response = await api.getOfflineFeeCollectionReport(params);
        if (response.data?.deposited_dates) {
          setDepositedDates(new Set(response.data.deposited_dates));
        }
        if (response.data?.deposit_slips) {
          setDepositSlips(response.data.deposit_slips);
        }
      }
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const toggleCashDeposit = async (date) => {
    if (depositedDates.has(date)) {
      // Unmark
      try {
        await api.unmarkCashDeposited(date);
        setDepositedDates(prev => { const next = new Set(prev); next.delete(date); return next; });
        setDepositSlips(prev => { const next = {...prev}; delete next[date]; return next; });
        toast.success(`Cash deposit mark removed for ${date}`);
      } catch (error) {
        toast.error('Failed to update deposit status');
      }
    } else {
      // Trigger file input for deposit slip
      if (fileInputRefs.current[date]) {
        fileInputRefs.current[date].click();
      }
    }
  };

  const handleDepositSlipUpload = async (date, file) => {
    if (!file) return;
    setUploadingDate(date);
    try {
      const res = await api.markCashDeposited(date, file);
      setDepositedDates(prev => new Set(prev).add(date));
      setDepositSlips(prev => ({...prev, [date]: res.data.deposit_slip_url}));
      toast.success(`Cash for ${date} marked as deposited with slip`);
    } catch (error) {
      toast.error('Failed to upload deposit slip');
    } finally {
      setUploadingDate(null);
    }
  };

  // Group cash payments by date for deposit checkboxes
  const getCashByDate = () => {
    if (!reportData?.payments) return [];
    const dateMap = {};
    for (const p of reportData.payments) {
      if (p.payment_mode === 'cash') {
        if (!dateMap[p.payment_date]) dateMap[p.payment_date] = { date: p.payment_date, amount: 0, count: 0 };
        dateMap[p.payment_date].amount += p.amount || 0;
        dateMap[p.payment_date].count += 1;
      }
    }
    return Object.values(dateMap).sort((a, b) => b.date.localeCompare(a.date));
  };

  const setQuickDateRange = (range) => {
    const today = new Date();
    let start, end;
    
    switch (range) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'week':
        start = new Date(today.setDate(today.getDate() - 7)).toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0];
        end = new Date().toISOString().split('T')[0];
        break;
      default:
        return;
    }
    
    setStartDate(start);
    setEndDate(end);
  };

  return (
    <div className="space-y-6" data-testid="offline-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Offline Centre Reports</h1>
          <p className="text-slate-600">View admission and fee collection reports</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('admissions')}
          className={`px-4 py-2 font-medium transition border-b-2 ${
            activeTab === 'admissions'
              ? 'text-teal-600 border-teal-600'
              : 'text-slate-600 border-transparent hover:text-slate-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <Users size={18} />
            Admission Report
          </div>
        </button>
        <button
          onClick={() => setActiveTab('fees')}
          className={`px-4 py-2 font-medium transition border-b-2 ${
            activeTab === 'fees'
              ? 'text-teal-600 border-teal-600'
              : 'text-slate-600 border-transparent hover:text-slate-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <IndianRupee size={18} />
            Fee Collection Report
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3">
          {/* Quick Date Selection */}
          <div className="flex gap-1">
            <button onClick={() => setQuickDateRange('today')} className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg">Today</button>
            <button onClick={() => setQuickDateRange('week')} className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg">This Week</button>
            <button onClick={() => setQuickDateRange('month')} className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg">This Month</button>
            <button onClick={() => setQuickDateRange('year')} className="px-3 py-1.5 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg">This Year</button>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          
          {activeTab === 'admissions' && (
            <>
              <select
                value={batchFilter}
                onChange={(e) => setBatchFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="">All Batches</option>
                {batches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </>
          )}
          
          <select
            value={counsellorFilter}
            onChange={(e) => setCounsellorFilter(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
          >
            <option value="">All Counsellors</option>
            {counsellors.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          
          {(startDate || endDate || batchFilter || counsellorFilter) && (
            <button
              onClick={() => { setStartDate(''); setEndDate(''); setBatchFilter(''); setCounsellorFilter(''); }}
              className="px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {!loading && reportData && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {activeTab === 'admissions' ? (
            <>
              <div className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Users className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Admissions</p>
                    <p className="text-2xl font-bold text-slate-800">{reportData.total_admissions}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-slate-100 rounded-lg">
                    <IndianRupee className="text-slate-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Fee</p>
                    <p className="text-2xl font-bold text-slate-800">₹{reportData.total_fee?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <IndianRupee className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Collected</p>
                    <p className="text-2xl font-bold text-green-600">₹{reportData.total_collected?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <IndianRupee className="text-red-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Pending</p>
                    <p className="text-2xl font-bold text-red-600">₹{reportData.total_pending?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileBarChart className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Transactions</p>
                    <p className="text-2xl font-bold text-slate-800">{reportData.total_payments}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border p-5">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <IndianRupee className="text-green-600" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Collected</p>
                    <p className="text-2xl font-bold text-green-600">₹{reportData.total_amount?.toLocaleString()}</p>
                  </div>
                </div>
              </div>
              {reportData.mode_totals && Object.entries(reportData.mode_totals).map(([mode, data]) => (
                <div key={mode} className="bg-white rounded-xl border p-5">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg ${MODE_COLORS[mode]?.bg || 'bg-slate-100'}`}>
                      {mode === 'cash' ? <Banknote className={MODE_COLORS[mode]?.text || 'text-slate-600'} size={24} /> :
                       mode === 'upi' ? <Smartphone className={MODE_COLORS[mode]?.text || 'text-slate-600'} size={24} /> :
                       <CreditCard className={MODE_COLORS[mode]?.text || 'text-slate-600'} size={24} />}
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">{MODE_LABELS[mode] || mode}</p>
                      <p className={`text-xl font-bold ${MODE_COLORS[mode]?.text || 'text-slate-800'}`}>₹{data.amount?.toLocaleString()}</p>
                      <p className="text-xs text-slate-400">{data.count} txns</p>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Report Data Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading report...</div>
        ) : activeTab === 'admissions' ? (
          // Admission Report Table
          reportData?.grouped_data?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Period</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Admissions</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Total Fee</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Collected</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Pending</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reportData.grouped_data.map((row, index) => (
                    <tr key={index} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-800">{row.date}</td>
                      <td className="px-4 py-3 text-right text-slate-600">{row.count}</td>
                      <td className="px-4 py-3 text-right text-slate-600">₹{row.total_fee?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-green-600 font-medium">₹{row.collected?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-red-600 font-medium">₹{row.pending?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500">No admissions found for the selected period</div>
          )
        ) : (
          // Fee Collection Report Table
          reportData?.payments?.length > 0 ? (
            <>
              {/* Cash Deposit Tracking */}
              {getCashByDate().length > 0 && (
                <div className="px-5 py-4 border-b border-slate-200 bg-amber-50">
                  <div className="flex items-center gap-2 mb-3">
                    <Banknote size={18} className="text-amber-600" />
                    <h4 className="font-semibold text-slate-800 text-sm">Cash Deposit Tracker</h4>
                    <span className="text-xs text-slate-500">(Upload deposit slip to mark as deposited)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {getCashByDate().map((item) => (
                      <div key={item.date} className={`flex items-center gap-3 px-3 py-3 rounded-lg border transition ${depositedDates.has(item.date) ? 'bg-green-50 border-green-300' : 'bg-white border-slate-200'}`} data-testid={`cash-deposit-${item.date}`}>
                        <input
                          type="checkbox"
                          checked={depositedDates.has(item.date)}
                          onChange={() => toggleCashDeposit(item.date)}
                          className="w-4 h-4 rounded border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer"
                          disabled={uploadingDate === item.date}
                        />
                        <input
                          type="file"
                          accept="image/*"
                          ref={el => fileInputRefs.current[item.date] = el}
                          onChange={(e) => handleDepositSlipUpload(item.date, e.target.files[0])}
                          className="hidden"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700">{item.date}</p>
                          <p className="text-xs text-slate-500">₹{item.amount.toLocaleString()} ({item.count} txns)</p>
                        </div>
                        {uploadingDate === item.date && (
                          <span className="text-xs text-amber-600 animate-pulse">Uploading...</span>
                        )}
                        {depositedDates.has(item.date) && (
                          <div className="flex items-center gap-1">
                            {depositSlips[item.date] && (
                              <button onClick={() => setPreviewSlip(depositSlips[item.date])} className="p-1 text-blue-500 hover:bg-blue-50 rounded" title="View Slip">
                                <Eye size={16} />
                              </button>
                            )}
                            <CheckSquare size={16} className="text-green-500 flex-shrink-0" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deposit Slip Preview Modal */}
              {previewSlip && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setPreviewSlip(null)}>
                  <div className="bg-white rounded-xl p-4 max-w-lg max-h-[80vh] overflow-auto relative" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setPreviewSlip(null)} className="absolute top-2 right-2 p-1 hover:bg-slate-100 rounded-full">
                      <X size={20} />
                    </button>
                    <h3 className="font-semibold text-slate-800 mb-3">Deposit Slip</h3>
                    <img src={previewSlip} alt="Deposit Slip" className="w-full rounded-lg" />
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Admission #</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Student</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Mode</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">UTR</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Collected By</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {reportData.payments.map((payment, index) => (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-slate-600">{payment.payment_date}</td>
                        <td className="px-4 py-3 font-mono text-teal-600">{payment.admission_number}</td>
                        <td className="px-4 py-3 font-medium text-slate-800">{payment.student_name}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${MODE_COLORS[payment.payment_mode]?.bg || 'bg-slate-100'} ${MODE_COLORS[payment.payment_mode]?.text || 'text-slate-600'}`}>
                            {MODE_LABELS[payment.payment_mode] || payment.payment_mode?.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-sm">{payment.utr_number || '-'}</td>
                        <td className="px-4 py-3 text-slate-600">{payment.collected_by_name}</td>
                        <td className="px-4 py-3 text-right text-green-600 font-medium">₹{payment.amount?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-slate-500">No payments found for the selected period</div>
          )
        )}
      </div>
    </div>
  );
};

export default OfflineReports;
